import { useMemo } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PortraitIcon from '@mui/icons-material/Portrait';
import { Box, IconButton, Slider, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Cropper from 'react-easy-crop';
import { MAX_PROFILE_PICTURE_SIZE_IN_MB, PROFILE_PICTURE, PROFILE_PICTURE_SPECS } from 'common/constants/user';
import { DropZone } from 'components';
import { TitleArray } from 'components/CircleIconWithText/types';
import { validImageExtensions } from 'components/Documents/imageTypes';
import { FileSrcProps } from 'components/DropZone/DropZone.props';
import { useImageUpload } from 'hooks/useImageUpload/useImageUpload';
// Separate constants file for image container-specific stuff
import { ACCEPT_FILE_TYPES, DEFAULT_CROP_SHAPE, DELETE_IMAGE_TEXT, MAX_ZOOM, MIN_ZOOM, SLIDER_STEP } from './constants';
import { ImageContainerProps } from './ImageContainer.props';
import {
  contentBoxSx,
  controlsGridSx,
  cropperBoxSx,
  cropperWrapperSx,
  rootStackSx,
  titleSx,
} from './ImageContainer.styles';
import { getTitleArray } from './utilities/getTitleArray';

const ImageContainer = ({
  imageSrc: externalImageSrc,
  setImageSrc,
  setSelectedFile,
  setCroppedAreaPixels,
  title = PROFILE_PICTURE,
  cropShape = DEFAULT_CROP_SHAPE,
  aspect,
}: ImageContainerProps) => {
  const { imageSrc, crop, zoom, setCrop, setZoom, onCropComplete, handleFileProcess, reset } = useImageUpload({
    onCropCompleteAction: setCroppedAreaPixels,
    initialImage: externalImageSrc,
  });

  const onDrop = async ({ file }: FileSrcProps) => {
    if (!file) {
      return;
    }
    const dataUrl = await handleFileProcess(file);
    setSelectedFile?.(file);
    setImageSrc(dataUrl);
  };

  const handleReset = () => {
    reset();
    setSelectedFile?.(null);
    setImageSrc(null);
  };

  const titleArray = useMemo(
    (): TitleArray => [
      ...getTitleArray(title),
    ],
    [title]
  );

  const calculatedAspect = aspect ? aspect.horizontal / aspect.vertical : 1;

  return (
    <Stack spacing={2} sx={rootStackSx}>
      <Typography variant="h6" component="h2" sx={titleSx}>
        {title}
      </Typography>

      <Box sx={contentBoxSx}>
        {imageSrc ? (
          <Box sx={cropperWrapperSx}>
            <Box sx={cropperBoxSx}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={calculatedAspect}
                cropShape={cropShape}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                showGrid={false}
                restrictPosition={false}
                // Allow the cropper to zoom out below 100% scale
                minZoom={MIN_ZOOM}
              />
            </Box>

            <Grid container spacing={2} alignItems="center" sx={controlsGridSx}>
              <Grid>
                <PortraitIcon color={zoom > MIN_ZOOM ? 'primary' : 'disabled'} fontSize="small" />
              </Grid>
              <Grid size="grow">
                <Slider
                  value={zoom}
                  // Allow the slider to reach the new zoom-out floor
                  min={MIN_ZOOM}
                  max={MAX_ZOOM}
                  step={SLIDER_STEP}
                  onChange={(_, val) => setZoom(val as number)}
                  aria-label="Zoom"
                />
              </Grid>
              <Grid>
                <PortraitIcon color={zoom < MAX_ZOOM ? 'primary' : 'disabled'} fontSize="medium" />
              </Grid>
              <Grid>
                <IconButton onClick={handleReset} size="small" color="error" title={DELETE_IMAGE_TEXT}>
                  <DeleteOutlineIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <DropZone
            setFileSrc={onDrop}
            subTitle={PROFILE_PICTURE_SPECS}
            acceptFileTypes={ACCEPT_FILE_TYPES}
            validFileTypes={validImageExtensions}
            maxSizeAllowedInMb={MAX_PROFILE_PICTURE_SIZE_IN_MB}
            titleArray={titleArray}
            allowMultipleFiles={false}
          />
        )}
      </Box>
    </Stack>
  );
};

export default ImageContainer;
